"use client";

import { StateCreator } from "zustand";
import {
  IActionItem,
  IAssessment,
  IBroadcast,
  ICarePlanTemplate,
} from "../components/testData/carePlanData";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import {
  ActionStatusEnum,
  PatientEnrollment,
} from "@clients/velys-infrastructure/api";
import { useUpdateEnrollmentActionItem } from "../hooks/usePatientEnrollment";

export type IEnrollmentItem = IActionItem &
  IBroadcast &
  IAssessment & { item_type?: string };

export type PatientEnrollmentState = {
  // patientEnrollmentId: string;
  patientEnrollment: PatientEnrollment | null;
  patientCareplan: ICarePlanTemplate | null;
  activePage: string;
  patientEnrollmentItem: IEnrollmentItem | null;
  selectedLibraryType: string;
  selectedLibraryTimeframe: string;
  currentUpdateIds: string[];
  // Actions
  setPatientEnrollment: (enrollment: PatientEnrollment | null) => void;
  setPatientCareplan: (careplan: ICarePlanTemplate | null) => void;
  setActivePage: (page: string) => void;
  setPatientEnrollmentItem: (actionItem: IEnrollmentItem | null) => void;
  setSelectedLibraryType: (type: string) => void;
  setSelectedLibraryTimeframe: (timeframe: string) => void;
  handleViewPage: (
    path: string,
    key: string,
    router: AppRouterInstance,
    locale?: string
  ) => void;

  addUpdateId: (id: string) => void;
  removeUpdateId: (id: string) => void;

  handleConfirmAction: (
    actionId: string,
    updateMutation: ReturnType<typeof useUpdateEnrollmentActionItem>,
    action: IActionItem
  ) => void;
};

export const createPatientEnrollmentSlice: StateCreator<
  PatientEnrollmentState
> = (set, get) => ({
  // patientEnrollmentId: null,
  patientEnrollment: null,
  patientCareplan: null,
  activePage: "home",
  patientEnrollmentItem: null,
  selectedLibraryType: "actions",
  selectedLibraryTimeframe: "Pre-Op",
  currentUpdateIds: [],

  // Actions
  setPatientEnrollment: (enrollment) => set({ patientEnrollment: enrollment }),
  setPatientCareplan: (careplan) => set({ patientCareplan: careplan }),
  setActivePage: (page) => set({ activePage: page }),
  setPatientEnrollmentItem: (actionItem) =>
    set({ patientEnrollmentItem: actionItem }),
  setSelectedLibraryType: (type) => set({ selectedLibraryType: type }),
  setSelectedLibraryTimeframe: (timeframe) =>
    set({ selectedLibraryTimeframe: timeframe }),

  handleViewPage: (
    path: string,
    key: string,
    router: AppRouterInstance,
    locale?: string
  ) => {
    if (locale) {
      router.push(`/${locale}${path}`);
    } else {
      router.push(`/en-US${path}`);
    }
    set({ activePage: key });
  },

  addUpdateId: (id) => {
    set((state) => ({
      currentUpdateIds: [...state.currentUpdateIds, id],
    }));
  },

  removeUpdateId: (id) => {
    set((state) => ({
      currentUpdateIds: state.currentUpdateIds.filter(
        (updateId) => updateId !== id
      ),
    }));
  },

  handleConfirmAction: (actionId, updateMutation, action) => {
    const {
      patientEnrollment,
      addUpdateId,
      removeUpdateId,
      setPatientEnrollmentItem,
      patientEnrollmentItem,
    } = get();
    if (!patientEnrollment) return;

    addUpdateId(actionId);

    const statusRequest = {
      status: "completed" as ActionStatusEnum,
    };

    updateMutation.mutate(
      {
        orgId: patientEnrollment?.org_id,
        enrollmentId: patientEnrollment?.enrollment.enrollment_id,
        actionId: actionId,
        actionUpdateStatusRequest: statusRequest,
      },
      {
        onSuccess: () => {
          const updatedActionItem = {
            ...action,
            status: ActionStatusEnum.Completed,
          };
          // only update patient enrollment w/ new status if modal open
          if (patientEnrollmentItem) {
            setPatientEnrollmentItem(updatedActionItem);
          }
          removeUpdateId(actionId);
        },
        onError: (error) => {
          removeUpdateId(actionId);
          console.error(error);
        },
      }
    );
  },
});
